import { useMemo } from 'react';
import type { TimelineItemProps } from 'antd';
import { Avatar, Flex, Space, Tag, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TicketWorkOrderEventDto } from '@evimo/client/models';
import {
  WORK_ORDER_UI,
  softBadgeStyle,
  executionResultTone,
} from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import {
  asOptionalString,
  ticketStatusTagColor,
} from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import {
  formatTicketEventDetail,
  formatTicketEventWhen,
  getTicketEventVisual,
  isPutOnHoldEvent,
  isResumedFromHoldEvent,
  isTicketTimelineEvent,
  shortActorId,
  ticketEventTitleKey,
} from '../utils';

export interface UseTicketDetailTimelineItemsArgs {
  events: TicketWorkOrderEventDto[];
  actorNameById: Map<string, string>;
  compact?: boolean;
}

export function useTicketDetailTimelineItems({
  events,
  actorNameById,
  compact = false,
}: UseTicketDetailTimelineItemsArgs): TimelineItemProps[] {
  const { t, i18n } = useTranslation('maintenance');

  return useMemo(
    () =>
      events
        .filter((event) => isTicketTimelineEvent(event.eventType))
        .map((event) => {
          const executionResult = asOptionalString(event.executionResult);
          const ticketStatus = asOptionalString(event.ticketStatus);
          const fromStatus = asOptionalString(event.fromStatus);
          const toStatus = asOptionalString(event.toStatus);
          const reason = asOptionalString(event.reason);
          const needsFollowUpRaw: unknown = event.needsFollowUp;
          const needsFollowUp = needsFollowUpRaw === true;
          const putOnHold = isPutOnHoldEvent({
            eventType: event.eventType,
            toStatus,
            reason,
          });
          const resumedFromHold = isResumedFromHoldEvent({
            eventType: event.eventType,
            fromStatus,
            toStatus,
          });
          const visual = getTicketEventVisual(
            event.eventType,
            executionResult,
            toStatus,
            reason,
          );
          const actorFromApi = asOptionalString(event.actorName);
          const actorLabel =
            actorFromApi ??
            actorNameById.get(event.actorId) ??
            shortActorId(event.actorId);
          const detail = formatTicketEventDetail(
            {
              eventType: event.eventType,
              reason,
              workOrderCode: asOptionalString(event.workOrderCode),
              fromWorkOrderCode: asOptionalString(event.fromWorkOrderCode),
              toWorkOrderCode: asOptionalString(event.toWorkOrderCode),
              fromStatus,
              toStatus,
            },
            t,
          );
          const showCompletionOutcome =
            event.eventType === 'COMPLETED' &&
            (executionResult != null || ticketStatus != null);
          const titleKey = ticketEventTitleKey(
            event.eventType,
            toStatus,
            fromStatus,
            reason,
          );

          return {
            color: visual.color,
            children: (
              <div style={{ paddingBottom: compact ? 2 : 8 }}>
                <Flex align="center" gap={8} wrap="wrap">
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: `${visual.color}18`,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {visual.icon}
                  </span>
                  <Typography.Text
                    strong
                    style={{ fontSize: compact ? 13 : 14, color: '#0f172a' }}
                  >
                    {t(titleKey, {
                      defaultValue: event.eventType,
                    })}
                  </Typography.Text>
                  {putOnHold ? (
                    <Tag
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        ...softBadgeStyle(WORK_ORDER_UI.status.ON_HOLD),
                      }}
                    >
                      {t('workOrders.kanban.columns.ON_HOLD')}
                    </Tag>
                  ) : null}
                  {resumedFromHold ? (
                    <Tag
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        ...softBadgeStyle(WORK_ORDER_UI.status.IN_PROGRESS),
                      }}
                    >
                      {t('workOrders.kanban.columns.IN_PROGRESS')}
                    </Tag>
                  ) : null}
                </Flex>

                <Flex
                  align="center"
                  gap={8}
                  wrap="wrap"
                  style={{ marginTop: 4, marginLeft: 36 }}
                >
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {formatTicketEventWhen(event.occurredAt, i18n.language)}
                  </Typography.Text>
                  <Space size={6} align="center">
                    <Tooltip
                      title={t('workOrders.ticketDetail.timeline.byActor', {
                        actor: actorLabel,
                      })}
                    >
                      <span>
                        <Avatar
                          size={20}
                          style={{
                            background: WORK_ORDER_UI.sidebarNavy,
                            fontSize: 11,
                          }}
                        >
                          {actorLabel.slice(0, 1).toUpperCase()}
                        </Avatar>
                      </span>
                    </Tooltip>
                    <Typography.Text style={{ fontSize: 12 }}>
                      {actorLabel}
                    </Typography.Text>
                  </Space>
                </Flex>

                {showCompletionOutcome ? (
                  <Space
                    size={6}
                    wrap
                    style={{ margin: '6px 0 0 36px', display: 'flex' }}
                  >
                    {executionResult ? (
                      <Tag
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          ...softBadgeStyle(
                            executionResultTone(executionResult),
                          ),
                        }}
                      >
                        {t('workOrders.ticketDetail.timeline.resultLabel', {
                          result: t(
                            `workOrders.executionResult.${executionResult}`,
                            { defaultValue: executionResult },
                          ),
                        })}
                      </Tag>
                    ) : null}
                    {ticketStatus ? (
                      <Tag
                        color={ticketStatusTagColor(ticketStatus)}
                        style={{ margin: 0 }}
                      >
                        {t(
                          'workOrders.ticketDetail.timeline.ticketStatusLabel',
                          {
                            status: t(
                              `workOrders.ticketStatus.${ticketStatus}`,
                              { defaultValue: ticketStatus },
                            ),
                          },
                        )}
                      </Tag>
                    ) : null}
                    {needsFollowUp ? (
                      <Tag color="gold" style={{ margin: 0 }}>
                        {t('workOrders.kanban.needsFollowUp')}
                      </Tag>
                    ) : null}
                  </Space>
                ) : null}

                {detail ? (
                  <Typography.Paragraph
                    style={{
                      margin: '6px 0 0 36px',
                      padding: '6px 10px',
                      background: putOnHold ? '#f5f3ff' : '#f8fafc',
                      borderRadius: 8,
                      border: putOnHold
                        ? '1px solid #ddd6fe'
                        : '1px solid #e2e8f0',
                      fontSize: 12,
                      color: '#334155',
                      fontWeight: putOnHold ? 600 : undefined,
                    }}
                  >
                    {detail}
                  </Typography.Paragraph>
                ) : null}
              </div>
            ),
          };
        }),
    [actorNameById, compact, events, i18n.language, t],
  );
}
